import React, {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import {
  cssFontStyle,
  cssFontWeight,
  cssString,
  cssUrl,
} from '../../utils/cssValues'

export type RegisteredFont = {
  name: string
  /** Direct font file URL → emitted as @font-face */
  url?: string
  /** External stylesheet URL (Typekit, Google Fonts, Adobe Fonts) → emitted as <link rel="stylesheet"> */
  stylesheetUrl?: string
  weight?: string
  style?: 'normal' | 'italic'
}

type FontRegistryValue = {
  fonts: RegisteredFont[]
  register: (font: RegisteredFont) => void
  unregister: (name: string) => void
}

const noop = () => {}

export const FontRegistryContext = createContext<FontRegistryValue>({
  fonts: [],
  register: noop,
  unregister: noop,
})

export const useFontRegistry = () => useContext(FontRegistryContext)

/**
 * The @font-face rule for a registered font, or '' when it has no name or no
 * usable URL. Every value is checked or escaped (utils/cssValues): the rule
 * is CSS text, so a name or URL with a quote in it could otherwise close the
 * rule and add others to the page (BR-5693). The URL must be https or
 * relative; an unknown weight or style falls back to `normal`.
 */
export const buildFontFace = (f: RegisteredFont) => {
  if (!f.name) return ''
  const src = cssUrl(f.url)
  if (!src) return ''
  const weight = cssFontWeight(f.weight) ?? 'normal'
  const style = cssFontStyle(f.style) ?? 'normal'
  return `@font-face { font-family: ${cssString(
    f.name,
  )}; src: ${src}; font-weight: ${weight}; font-style: ${style}; font-display: swap; }`
}

export const FontRegistryProvider = ({ children }: PropsWithChildren<{}>) => {
  const [fonts, setFonts] = useState<RegisteredFont[]>([])

  const register = useCallback((font: RegisteredFont) => {
    if (!font.name) return
    if (!font.url && !font.stylesheetUrl) return
    setFonts(prev => {
      const idx = prev.findIndex(f => f.name === font.name)
      if (idx === -1) return [...prev, font]
      const next = [...prev]
      next[idx] = font
      return next
    })
  }, [])

  const unregister = useCallback((name: string) => {
    setFonts(prev => prev.filter(f => f.name !== name))
  }, [])

  const value = useMemo(
    () => ({ fonts, register, unregister }),
    [fonts, register, unregister],
  )

  return (
    <FontRegistryContext.Provider value={value}>
      {children}
    </FontRegistryContext.Provider>
  )
}

export const renderFontTag = (f: RegisteredFont): React.ReactNode => {
  if (!f.name) return null
  if (f.stylesheetUrl) {
    return <link rel="stylesheet" href={f.stylesheetUrl} data-font-registry />
  }
  const fontFace = buildFontFace(f)
  if (fontFace) {
    return (
      <style
        data-font-registry
        dangerouslySetInnerHTML={{ __html: fontFace }}
      />
    )
  }
  return null
}
